import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './VendorsList.css';

function VendorsList() {
  const [vendors, setVendors] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10, total: 0, totalPages: 1 });
  const navigate = useNavigate();

  const fetchVendors = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/vendors', {
        params: {
          page,
          pageSize,
          search: searchQuery.trim(),
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setVendors(response.data.vendors || []);
      setPagination(response.data.pagination || { page, pageSize, total: 0, totalPages: 1 });
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to load vendors.');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchQuery]);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  return (
    <div className="vendors-page app-fixed-page">
      <div className="vendors-header app-page-header">
        <div className="app-title-group">
          <button className="back-link app-back-button" onClick={() => navigate('/')} aria-label="Back to home">
            Back
          </button>
          <div className="app-title-copy">
            <h2>Vendors</h2>
          <p>Manage vendor records, search suppliers, and add new vendor entries.</p>
          </div>
        </div>
        <div className="app-header-actions">
          <button className="btn-new-vendor" onClick={() => navigate('/vendors/new')}>
            + New Vendor
          </button>
        </div>
      </div>

      <div className="vendors-toolbar">
        <input
          className="vendors-search"
          type="text"
          value={searchQuery}
          onChange={(e) => {
            setPage(1);
            setSearchQuery(e.target.value);
          }}
          placeholder="Search by company, supplier code, phone, email, ledger"
        />
        <div className="vendors-actions">
          <select
            className="vendors-page-size"
            value={pageSize}
            onChange={(e) => {
              setPage(1);
              setPageSize(parseInt(e.target.value, 10));
            }}
          >
            <option value={10}>10 rows</option>
            <option value={20}>20 rows</option>
            <option value={40}>40 rows</option>
          </select>
          <button className="vendors-action" onClick={fetchVendors}>Refresh</button>
        </div>
      </div>

      <div className="app-scroll-results">
        <div className="vendors-table-wrapper app-grid-scroll">
          {loading ? (
            <div className="vendors-loading">Loading vendors...</div>
          ) : error ? (
            <div className="vendors-error">{error}</div>
          ) : (
            <table className="vendors-table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Street</th>
                  <th>State</th>
                  <th>Postal Code</th>
                  <th>Contact</th>
                  <th>City</th>
                  <th>Email</th>
                  <th>Phone 1</th>
                  <th>Phone 2</th>
                  <th>Supplier Code</th>
                  <th>Ledger</th>
                  <th>Due</th>
                  <th>Over Payment</th>
                  <th>Is Send</th>
                </tr>
              </thead>
              <tbody>
                {vendors.length === 0 ? (
                  <tr>
                    <td colSpan="14" className="empty-message">
                      No vendors found. Add a new vendor to get started.
                    </td>
                  </tr>
                ) : (
                  vendors.map((vendor) => (
                    <tr key={vendor.VendorId}>
                      <td>{vendor.company}</td>
                      <td>{vendor.street || '-'}</td>
                      <td>{vendor.state || '-'}</td>
                      <td>{vendor.postal_code || '-'}</td>
                      <td>{[vendor.first_name, vendor.last_name].filter(Boolean).join(' ') || '-'}</td>
                      <td>{vendor.city || '-'}</td>
                      <td>{vendor.email || '-'}</td>
                      <td>{vendor.phone1 || '-'}</td>
                      <td>{vendor.phone2 || '-'}</td>
                      <td>{vendor.supplier_code || '-'}</td>
                      <td>{vendor.Ledger || '-'}</td>
                      <td>{vendor.due ?? 0}</td>
                      <td>{vendor.over_payment ?? 0}</td>
                      <td>{vendor.IsSend ? 'Yes' : 'No'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {!loading && !error && (
          <div className="vendors-pagination">
            <div className="vendors-page-info">
              Showing page {pagination.page} of {pagination.totalPages} | Total rows: {pagination.total}
            </div>
            <div className="vendors-page-buttons">
              <button
                className="vendors-action"
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                disabled={pagination.page <= 1}
              >
                Previous
              </button>
              <button
                className="vendors-action"
                onClick={() => setPage((prev) => Math.min(prev + 1, pagination.totalPages))}
                disabled={pagination.page >= pagination.totalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default VendorsList;
